<template>
  <div class="font-sans" :class="containerClasses">
    <span
      v-if="variantConfig.icon"
      class="inline-flex items-center justify-center size-[28px] shrink-0"
    >
      <i
        :class="['fa-solid', variantConfig.icon, 'text-[14px] leading-none']"
        aria-hidden="true"
      ></i>
    </span>
    <span :class="textClasses">
      <slot />
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

type Variant = 'caution' | 'positive' | 'accent' | 'neutral';
type Size = 'small' | 'medium' | 'large' | 'notice';
type Direction = 'vertical' | 'horizon';

const props = withDefaults(
  defineProps<{
    /** 種類。注意喚起 / 肯定 / 注目 */
    variant?: Variant;
    size?: Size;
    /** vertical: アイコン横並び / horizon: アイコンを上に縦積み（中央寄せ） */
    direction?: Direction;
    /** vertical 時にテキストの折り返しを許可する（既定: 折り返さず1行 = Figma準拠） */
    wrap?: boolean;
  }>(),
  { variant: 'caution', size: 'medium', direction: 'vertical', wrap: false }
);

// variant ごとの背景・文字/アイコン色・Font Awesome アイコン・字幅
const VARIANTS: Record<
  Variant,
  { bg: string; color: string; icon: string; weight: string }
> = {
  caution: {
    bg: 'bg-fig-red-50',
    color: 'text-fig-red-800',
    icon: 'fa-triangle-exclamation',
    weight: 'font-bold',
  },
  positive: {
    bg: 'bg-fig-green-50',
    color: 'text-fig-green-700',
    icon: 'fa-circle-check',
    weight: 'font-bold',
  },
  accent: {
    bg: 'bg-pink-50',
    color: 'text-pink-600',
    icon: 'fa-circle-check',
    weight: 'font-bold',
  },
  // アイコンなし・本文色のまま置く注記用。icon が空の variant はアイコン要素ごと出さない
  neutral: {
    bg: 'bg-fig-gray-100',
    color: 'text-ink',
    icon: '',
    weight: 'font-normal',
  },
};

// direction=vertical（アイコン横並び）の size 別 余白・テキスト
// 左右の余白も size 側で持つ（notice だけ px 揃えのため、共通の pl-2 pr-4 に混ぜられない）
const VERTICAL_SIZE: Record<Size, { pad: string; text: string }> = {
  large: { pad: 'pl-2 pr-4 py-6', text: 'text-[20px] leading-[20px]' },
  medium: { pad: 'pl-2 pr-4 py-2', text: 'text-[16px] leading-[20px]' },
  small: { pad: 'pl-2 pr-4 py-1', text: 'text-[12px] leading-[16px]' },
  // Figma information_message（保険画面の注意書き）
  notice: { pad: 'p-5', text: 'text-[14px] leading-[20px]' },
};

// notice は vertical 専用の Figma 指定。horizon との組み合わせは定義しない
type HorizonSize = Exclude<Size, 'notice'>;

// direction=horizon（アイコン縦積み・中央寄せ）の size 別 余白・テキスト
// large は Figma 未定義のため medium を基準に 20px へ拡張
const HORIZON_SIZE: Record<HorizonSize, { pad: string; text: string }> = {
  large: { pad: 'gap-2 pt-2 pb-6 px-4', text: 'text-[20px] leading-[24px]' },
  medium: { pad: 'gap-2 pt-2 pb-4 px-4', text: 'text-[16px] leading-[24px]' },
  small: { pad: 'gap-1 pt-1 pb-3 px-2', text: 'text-[14px] leading-[20px]' },
};

const variantConfig = computed(() => VARIANTS[props.variant]);

const horizonSize = computed(() =>
  props.size === 'notice' ? HORIZON_SIZE.medium : HORIZON_SIZE[props.size]
);

const containerClasses = computed(() => {
  const v = variantConfig.value;
  if (props.direction === 'horizon') {
    return [
      'flex flex-col items-center justify-center rounded-[4px]',
      v.bg,
      v.color,
      horizonSize.value.pad,
    ];
  }
  return [
    // items-centerは折り返し時にテキストブロック全体の中央(=行と行の境界)へ
    // アイコンを揃えてしまい、実機確認でも明確にズレて見える。items-baseline
    // ならアイコン/テキストとも1行目のベースラインで揃うため、折り返しの
    // 有無に関わらず崩れない。
    'flex items-baseline gap-0 rounded-[4px]',
    v.bg,
    v.color,
    VERTICAL_SIZE[props.size].pad,
  ];
});

const textClasses = computed(() => {
  const weight = variantConfig.value.weight;
  if (props.direction === 'horizon') {
    return [weight, 'text-center', horizonSize.value.text];
  }
  return [
    weight,
    props.wrap ? 'min-w-0' : 'whitespace-nowrap',
    VERTICAL_SIZE[props.size].text,
  ];
});
</script>
